import { useState } from "react";
import { useEffect } from "react";
import { useOasisBackend } from "../../hooks/useOasisBackend";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ReadDatabase } from "../../api/OasisBackendApi";
import EditDatabase from "../../components/databaseComponenets/EditDatabase";
import ApplicationNavbar from "../../components/ApplicationNavbar";
import { cn } from "../../utils/cn";
import SearchPlayground from "../../components/databaseComponenets/SearchPlayground";
import BenchmarkView from "../../components/databaseComponenets/BenchmarkView";
import GettingStarted from "../../components/databaseComponenets/GettingStarted";
import { useUser } from "../../hooks/useUser";
import { ToastContainer } from "react-toastify";

const ManageDatabase = () => {
    const { Database_id } = useParams();
    const user = useUser();
    const navigate = useNavigate();
    const db = useOasisBackend();
    const [tab, setTab] = useState<"edit" | "test"  | "getting_started" | "benchmark">("getting_started");
    const [isLoading, setIsLoading] = useState(true);

    const [Database, setDatabase] = useState<ReadDatabase | null>(null);

    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        const initialTab = searchParams.get('tab');
        if (initialTab) {
            setTab(initialTab as 'edit' | 'test' | 'getting_started' | 'benchmark');
        }
    }, [searchParams]);

    useEffect(() => {
        setSearchParams(params => ({...Object.fromEntries(params.entries()), tab: tab}));
    }, [tab]);


    useEffect(() => {
        if (!Database) {
            return;
        }
        if (user.state !== "LoggedIn" || user.user?.email !== Database.owner) {
            console.log("Not logged in or not owner");
            if(Database.is_public) {
                setTab("test");
                return;
            }else{
                navigate("/playground");
            }
        }
    }, [Database]);

    useEffect(() => {
        if(!Database_id) {
            navigate("/playground")
            return;
        }
        const fetchDatabase = async () => {
            try {
                setIsLoading(true);
                const response = await db.endpoints.api.apiDatabasesRetrieve(Database_id);
                setDatabase(response.data);
            } catch (error) {
                console.error("Error fetching database:", error);
                navigate("/playground");
            } finally {
                setIsLoading(false);
            }
        }
        fetchDatabase();
    }, [Database_id]);

    if(!Database_id) {
        navigate("/playground")
        return <div>No Database id</div>
    }

    // Loading screen
    if (isLoading) {
        return (
            <div className="flex flex-col min-h-screen pt-16 bg-gray-900 text-white">
                <ApplicationNavbar />
                <div className="flex items-center justify-center flex-1">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                        <p className="text-lg">Loading database...</p>
                    </div>
                </div>
            </div>
        );
    }

    const isOwner = user.state === "LoggedIn" && user.user?.email === Database?.owner;
    
    return(
        <div className="flex flex-col min-h-screen pt-16 gap-2 bg-gray-900 text-white">
            <ApplicationNavbar />
            <ToastContainer 
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                theme="dark"
            />
            <div className="flex flex-col gap-2">
                <div className="flex flex-row gap-2 w-full justify-evenly py-8">
                    {isOwner && <button className={cn("p-2 rounded-md", tab === "getting_started" && "bg-gray-400")} onClick={() => setTab("getting_started")}>Getting Started</button>}
                    {isOwner && <button className={cn("p-2 rounded-md", tab === "edit" && "bg-gray-400")} onClick={() => setTab("edit")}>Edit</button>}
                    <button className={cn("p-2 rounded-md", tab === "test" && "bg-gray-400")} onClick={() => setTab("test")}>Test</button>
                    {isOwner && <button className={cn("p-2 rounded-md", tab === "benchmark" && "bg-gray-400")} onClick={() => setTab("benchmark")}>Benchmark</button>}
                    <button 
                        className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors border border-blue-500"
                        onClick={() => navigate(`/knowledge-base/${Database_id}/analysis`)}
                    >
                        📊 Analysis
                    </button>
                </div>
                {tab === "edit" && isOwner && Database && <EditDatabase Database={Database} setDatabase={setDatabase} />}
                {tab === "test" && Database && <SearchPlayground isOwner={isOwner} Database_id={Database.id} oasis_enabled={Database.oasis_enabled} />}
                {tab === "getting_started" && isOwner && Database && <GettingStarted database={Database} />}
                {tab === "benchmark" && Database && <BenchmarkView Database_id={Database.id} isOwner={isOwner} />}
            </div>
        </div>
    )
}

export default ManageDatabase;